'use client'

import React, { useState } from 'react'
import { Box, Flex, Stack, Title } from '@mantine/core'
import { motion } from 'framer-motion'
import { getCategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import type { FAQData } from '@/lib/hardcoded-data/CategoryPages/types'
import NumberedAccordionItem from '@/components/Refresh/NumberedAccordion/NumberedAccordionItem'
import { textFadeInVariantsNoTransition, staggerContainer } from '@/lib/animations'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import type { CategoryWidgetProps } from '../types'

export default function WidgetFAQ({ categoryName, data: dataProp }: CategoryWidgetProps) {
    const data = (dataProp ?? getCategoryPageData(categoryName, 'faq')) as FAQData | null
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    if (!data) return null

    const { title, items } = data

    return (
        <Box
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px 0px' }}
            variants={staggerContainer}
            id="faqs"
            py={48}
            px={{ base: 16, sm: 'auto' }}
            // px={{ base: 24, sm: 48 }}
            maw={1400}
            mx="auto"
            w="100%"
        >
            <Flex
                direction={{ base: 'column', sm: 'row' }}
                gap={{ base: 32, sm: 60 }}
                align={{ base: 'stretch', sm: 'flex-start' }}
                wrap="nowrap"
            >
                {/* Left: Title (~42% of content width per design) */}
                <MotionBox variants={textFadeInVariantsNoTransition} style={{ flex: '1 1 42%', minWidth: 0 }}>
                    <Title
                        order={2}
                        c="gray.9"
                        lh="var(--gm-refresh-title-xl-lh)"
                        m={0}
                        fz={{ base: 'var(--gm-refresh-title-md)', sm: 'var(--gm-refresh-title-xl)' }}
                        lts={-1.6}
                        maw={620}
                    >
                        {title}
                    </Title>
                </MotionBox>

                {/* Right: Accordion (~58% of content width per design) */}
                <MotionBox
                    variants={textFadeInVariantsNoTransition}
                    style={{ flex: '1 1 58%', minWidth: 0, maxWidth: 600, margin: '0 auto' }}
                >
                    <Stack gap={0}>
                        {items.map((item, index) => (
                            <NumberedAccordionItem
                                key={`faq-${index}`}
                                number={index + 1}
                                title={item.question}
                                content={item.answer}
                                isOpen={openIndex === index}
                                onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                                hideTopBorder={false}
                                showNumber={false}
                                contentStyle={{
                                    fz: 'lg',
                                    fw: 500,
                                    lh: '140%',
                                    ml: 0,
                                    pt: 12,
                                }}
                            />
                        ))}
                    </Stack>
                </MotionBox>
            </Flex>
        </Box>
    )
}
